import { createBrowserRouter, Navigate, redirect, RouterProvider } from 'react-router';
import Login from './components/login/Login';
import CustomerLogin from './components/customer/login/CustomerLogin';
import { roleGuard } from './guards/AuthGuard';
import {
  LOGIN,
  SIGN_UP,
  CUSTOMER_LOGIN,
  CUSTOMER_DASHBOARD,
} from './services/constants/URIConstants';
import AdminStickLayout from './layout/AdminStickLayout';
import LendingDashboard from './components/dashboard/LendingDashboard';
import CustomerManagement from './components/customers/CustomerManagement';
import CustomerDebitsView from './components/customers/CustomerDebitsView';
import CustomerInterestOutstandingReport from './components/customers/interest/CustomerInterestOutstandingReport';
import BlacklistedCustomers from './components/customers/BlacklistedCustomers';
import EligibilityCheck from './components/admin/eligibility/EligibilityCheck';
import NetWorthTracker from './components/admin/networth/NetWorthTracker';
import CustomerDetailsView from './components/customers/CustomerDetailsView';
import EditCustomerForm from './components/customers/EditCustomerForm';
import CashflowDashboard from './components/admin/cashflow/CashflowDashboard';
import LeadsManagement from './components/admin/leads/LeadsManagement';
import ProductManagement from './components/admin/products/ProductManagement';
import PaymentsAndDebits from './components/admin/payments/PaymentsAndDebits';
import InterestRequestsManagement from './components/admin/interest-requests/InterestRequestsManagement';
import ReferralsManagement from './components/admin/referrals/ReferralsManagement';
import FundRequestsManagement from './components/admin/fund-requests/FundRequestsManagement';
import AnnualFinancialSummary from './components/admin/reports/AnnualFinancialSummary';
import RevenueCashflowReport from './components/admin/reports/RevenueCashflowReport';
import InterestCollectedCurrentMonth from './components/admin/reports/InterestCollectedCurrentMonth';
import OflyncCashflowSync from './components/admin/reports/OflyncCashflowSync';
import MonthlyPaymentTransactionsSummary from './components/admin/reports/MonthlyPaymentTransactionsSummary';
import CustomerLendingAudit from './components/admin/reports/CustomerLendingAudit';
import CapitalEfficiencyDashboard from './components/admin/reports/CapitalEfficiencyDashboard';
import MonthlyCapitalEfficiencyTracking from './components/admin/reports/MonthlyCapitalEfficiencyTracking';
import MonthlyKpiSnapshots from './components/admin/reports/MonthlyKpiSnapshots';
import CustomerDepositsMonitoring from './components/admin/deposits/CustomerDepositsMonitoring';
import WhatsappPaymentRemindersAdmin from './components/admin/whatsapp/WhatsappPaymentRemindersAdmin';
import Settings from './components/admin/settings/Settings';
import HomeScreen from './components/home/HomeScreen';
import ServicesPage from './components/services/ServicesPage';
import SignupForm from './components/signup/SignupForm';
import CustomerPortalLayout from './components/customer/portal/CustomerPortalLayout';
import PortalHome from './components/customer/portal/pages/PortalHome';
import PortalDebits from './components/customer/portal/pages/PortalDebits';
import PortalDebitPayments from './components/customer/portal/pages/PortalDebitPayments';
import PortalPayments from './components/customer/portal/pages/PortalPayments';
import PortalDeposits from './components/customer/portal/pages/PortalDeposits';
import PortalProducts from './components/customer/portal/pages/PortalProducts';
import PortalNotifications from './components/customer/portal/pages/PortalNotifications';
import PortalProfile from './components/customer/portal/pages/PortalProfile';
import PortalRequestFunds from './components/customer/portal/pages/PortalRequestFunds';
import PortalReferrals from './components/customer/portal/pages/PortalReferrals';
import PublicGuidePage, { GuideContent } from './components/customer/portal/pages/PortalGuide';



export default function AppRoutes(props: any): React.ReactElement {
  const isDevelopment = process.env.NODE_ENV !== 'production';
  
  const baseRoutes = [
    {
      path: '/',
      Component: HomeScreen,
    },
    {
      path: '/services',
      Component: ServicesPage,
    },
    { 
      path: 'admin',
      Component: AdminStickLayout,
      loader: roleGuard.adminProtectedLoader, // Only allow admin access
      children: [
        {
          path: 'dashboard',
          Component: LendingDashboard,
          loader: roleGuard.adminProtectedLoader,
        },
        {
          path: 'customers',
          Component: CustomerManagement,
          loader: roleGuard.adminProtectedLoader,
        },
        {
          path: 'blacklisted-customers',
          Component: BlacklistedCustomers,
          loader: roleGuard.adminProtectedLoader,
        },
        {
          path: 'eligibility-check',
          Component: EligibilityCheck,
          loader: roleGuard.adminProtectedLoader,
        },
        {
          path: 'networth',
          Component: NetWorthTracker,
          loader: roleGuard.adminProtectedLoader,
        },
        {
          path: 'customers/:customerId',
          Component: CustomerDetailsView,
          loader: roleGuard.adminProtectedLoader,
        },
        {
          path: 'customers/:customerId/edit',
          Component: EditCustomerForm,
          loader: roleGuard.adminProtectedLoader,
        },
        {
          path: 'customers/:customerId/debits',
          Component: CustomerDebitsView,
          loader: roleGuard.adminProtectedLoader,
        },
        {
          path: 'customers/:customerId/interest-report',
          Component: CustomerInterestOutstandingReport,
          loader: roleGuard.adminProtectedLoader,
        },
        {
          path: 'deposits-monitoring',
          Component: CustomerDepositsMonitoring,
          loader: roleGuard.adminProtectedLoader,
        },
        {
          path: 'whatsapp-payment-reminders',
          Component: WhatsappPaymentRemindersAdmin,
          loader: roleGuard.adminProtectedLoader,
        },
        {
          path: 'cashflow',
          Component: CashflowDashboard,
          loader: roleGuard.adminProtectedLoader,
        },
        {
          path: 'leads',
          Component: LeadsManagement,
          loader: roleGuard.adminProtectedLoader,
        },
        {
          path: 'products',
          Component: ProductManagement,
          loader: roleGuard.adminProtectedLoader,
        },
        {
          path: 'payments',
          Component: PaymentsAndDebits,
          loader: roleGuard.adminProtectedLoader,
        },
        {
          path: 'financial-summary',
          Component: AnnualFinancialSummary,
          loader: roleGuard.adminProtectedLoader,
        },
        {
          path: 'monthly-kpi-snapshots',
          Component: MonthlyKpiSnapshots,
          loader: roleGuard.adminProtectedLoader,
        },
        {
          path: 'revenue-cashflow',
          Component: RevenueCashflowReport,
          loader: roleGuard.adminProtectedLoader,
        },
        {
          path: 'interest-collected-month',
          Component: InterestCollectedCurrentMonth,
          loader: roleGuard.adminProtectedLoader,
        },
        {
          path: 'oflync-cashflow',
          Component: OflyncCashflowSync,
          loader: roleGuard.adminProtectedLoader,
        },
        {
          path: 'monthly-payment-transactions',
          Component: MonthlyPaymentTransactionsSummary,
          loader: roleGuard.adminProtectedLoader,
        },
        {
          path: 'customer-lending-audit',
          Component: CustomerLendingAudit,
          loader: roleGuard.adminProtectedLoader,
        },
        {
          path: 'capital-efficiency',
          Component: CapitalEfficiencyDashboard,
          loader: roleGuard.adminProtectedLoader,
        },
        {
          path: 'monthly-capital-efficiency',
          Component: MonthlyCapitalEfficiencyTracking,
          loader: roleGuard.adminProtectedLoader,
        },
        {
          path: 'interest-requests',
          Component: InterestRequestsManagement,
          loader: roleGuard.adminProtectedLoader,
        },
        {
          path: 'referrals',
          Component: ReferralsManagement,
          loader: roleGuard.adminProtectedLoader,
        },
        {
          path: 'fund-requests',
          Component: FundRequestsManagement,
          loader: roleGuard.adminProtectedLoader,
        },
        {
          path: 'settings',
          Component: Settings,
          loader: roleGuard.adminProtectedLoader,
        },
      ],
    },
    {
      path: 'customer',
      Component: CustomerPortalLayout,
      loader: roleGuard.customerProtectedLoader,
      children: [
        { index: true, loader: () => redirect(CUSTOMER_DASHBOARD) },
        { path: 'dashboard', Component: PortalHome },
        { path: 'debits', Component: PortalDebits },
        { path: 'debits/:debitId/payments', Component: PortalDebitPayments },
        { path: 'payments', Component: PortalPayments },
        { path: 'deposits', Component: PortalDeposits },
        { path: 'products', Component: PortalProducts },
        { path: 'notifications', Component: PortalNotifications },
        { path: 'profile', Component: PortalProfile },
        { path: 'request-funds', Component: PortalRequestFunds },
        { path: 'referrals', Component: PortalReferrals },
        { path: 'help', Component: GuideContent },
      ],
    },
    {
      path: LOGIN,
      Component: Login
    },
    {
      path: '/customer/guide',
      Component: PublicGuidePage
    },
    {
      path: CUSTOMER_LOGIN,
      Component: CustomerLogin
    },
  ];

  // Only add signup route in development
  if (isDevelopment) {
    baseRoutes.push({
      path: SIGN_UP,
      Component: SignupForm
    });
  }

  baseRoutes.push({
    path: '*',
    Component: HomeScreen,
  });

  const routes = createBrowserRouter(baseRoutes);

  return <RouterProvider router={routes} />;
}
